import { Component, Inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog'
import { SharedModule } from '@app/shared/shared.module'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { CKEditorModule } from '@ckeditor/ckeditor5-angular'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { NgxSliderModule } from '@angular-slider/ngx-slider'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Role } from '@app/features/scoping/models/role.model'
import { Department } from '@app/features/scoping/models/department.model'
import { MatCheckboxChange } from '@angular/material/checkbox'
import { cloneDeep } from 'lodash'
import { Store } from '@ngrx/store'
import { LibraryTaskTemplate } from '@app/features/scoping/models/deliverable.model'
import { CompanyManagementActions } from '@app/features/company-management/store/actions/company-management.actions'

export interface AddLibraryTaskRolesModalConfig {
  title: string
  rateCard: RatecardVersion
  task: LibraryTaskTemplate
}

@Component({
  selector: 'add-library-task-roles-modal',
  standalone: true,
  imports: [CommonModule, SharedModule, MatDatepickerModule, CKEditorModule, ScopeUiInputComponent, NgxSliderModule],
  templateUrl: './add-library-task-roles-modal.component.html',
  styleUrls: ['./add-library-task-roles-modal.component.scss'],
})
export class AddLibraryTaskRolesModalComponent implements OnInit {
  searchText: string
  openCloseAll: boolean
  roles: number[]
  initialRoles: Role[]
  ratecard: RatecardVersion
  currentTask: LibraryTaskTemplate

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddLibraryTaskRolesModalConfig,
    private store: Store,
    public dialog: MatDialog
  ) {
    this.roles = []
    this.initialRoles = []
  }

  ngOnInit(): void {
    this.ratecard = cloneDeep(this.data.rateCard)
    this.currentTask = cloneDeep(this.data.task)
    this.roles.push(...this.data.task.roles.map((r) => r.rateCardRole.id))
    this.initialRoles.push(...this.data.task.roles)
  }

  onSearch(event: string) {
    if (!event) {
      this.openCloseAll = false
      this.ratecard.departments.forEach((d) => (d.selected = this.openCloseAll))
      return
    }
    this.searchText = event
    this.openCloseAll = true
    this.ratecard.departments.forEach((d) => (d.selected = this.openCloseAll))
  }

  hideRole(item: Role) {
    if (this.searchText?.length) {
      return !item.name.toLowerCase().includes(this.searchText.toLowerCase())
    }
    return false
  }

  hideDepartment(dep: Department) {
    return dep.roles.every((r) => this.hideRole(r))
  }

  expandAll() {
    this.openCloseAll = !this.openCloseAll
    this.ratecard.departments.forEach((d) => (d.selected = this.openCloseAll))
  }

  checkRole(role: Role) {
    if (this.roleChecked(role)) {
      this.roles = this.roles.filter((r) => r !== role.id)
    } else {
      this.roles.push(role.id)
    }
  }

  roleChecked(role: Role) {
    return this.roles.includes(role.id)
  }

  checkDepartment(dep: Department) {
    if (this.departmentChecked(dep)) {
      this.roles = this.roles.filter((r) => !dep.roles.map((dr) => dr.id).includes(r))
    } else {
      this.roles = this.roles.filter((r) => !dep.roles.map((dr) => dr.id).includes(r))
      this.roles.push(...dep.roles.map((dr) => dr.id))
    }
  }

  checkAll(event: MatCheckboxChange) {
    this.roles = []
    if (event.checked) {
      this.roles.push(...this.ratecard.departments.flatMap((d) => d.roles.map((dr) => dr.id)))
    }
  }

  departmentChecked(dep: Department) {
    return dep.roles.every((role) => this.roles.includes(role.id))
  }

  depPartChecked(dep: Department) {
    return (
      dep.roles.some((role) => this.roles.includes(role.id)) && !dep.roles.every((role) => this.roles.includes(role.id))
    )
  }

  departmentRolesCount(dep: Department) {
    return this.roles.filter((r) => dep.roles.map((r) => r.id).includes(r)).length
  }

  submit() {
    const initialRoleIds = this.initialRoles.map((r) => r.rateCardRole.id)

    const removedRoles = initialRoleIds.filter((id) => !this.roles.includes(id))

    const newRoles = this.roles
      .filter((roleId) => !initialRoleIds.includes(roleId))
      .map((roleId) => {
        const newRole = new Role()
        newRole.minutes = 0
        newRole.rateCardRole = { id: roleId }
        return newRole
      })

    this.currentTask.roles = [
      ...this.currentTask.roles.filter((r) => !removedRoles.includes(r.rateCardRole.id)),
      ...newRoles,
    ]

    this.store.dispatch(CompanyManagementActions.updateLibraryTask({ task: this.currentTask }))
    this.dialog.closeAll()
  }
}
